import {clamp} from 'lodash';
import {useCallback} from 'react';

import {hotkeyPressed} from '../../../util/hotkeys';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';

/**
 * Custom hook that provides keyboard handling functions for the modal.
 * This allows components to handle their own keyboard logic while sharing
 * the same underlying context data and update functions.
 */
export const useModalKeyboardHandling = () => {
  const {
    navigation,
    selectRun,
    updateStep,
    updateIndex,
    sliderData,
    sliderConfig,
    closeModal,
  } = useMediaBrowserModalContext();

  /**
   * Handles ESC key to close the modal
   */
  const handleEscapeKey = useCallback(
    (e: KeyboardEvent): boolean => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
        return true;
      }
      return false;
    },
    [closeModal]
  );

  /**
   * Handles step slider keyboard navigation (Meta + Arrow keys)
   */
  const handleStepSliderKeys = useCallback(
    (e: KeyboardEvent): boolean => {
      const {stepSlider} = sliderData;

      if (!stepSlider || stepSlider.max <= 0) {
        return false;
      }

      if (hotkeyPressed(e, 'nextStep')) {
        e.preventDefault();
        e.stopPropagation();
        const idx = stepSlider.steps?.indexOf(stepSlider.current) ?? 0;
        const newIdx = clamp(
          idx + sliderConfig.stepStrideLength,
          0,
          stepSlider.steps?.length - 1
        );
        const newValue = stepSlider.steps?.[newIdx] ?? stepSlider.current;
        updateStep(newValue);
        return true;
      }

      if (hotkeyPressed(e, 'prevStep')) {
        e.preventDefault();
        e.stopPropagation();
        const idx = stepSlider.steps?.indexOf(stepSlider.current) ?? 0;
        const newIdx = clamp(
          idx - sliderConfig.stepStrideLength,
          0,
          stepSlider.steps?.length - 1
        );
        const newValue = stepSlider.steps?.[newIdx] ?? stepSlider.current;
        updateStep(newValue);
        return true;
      }
      return false;
    },
    [sliderData, sliderConfig, updateStep]
  );

  /**
   * Handles index slider keyboard navigation (Alt + Arrow keys)
   */
  const handleIndexSliderKeys = useCallback(
    (e: KeyboardEvent): boolean => {
      const {indexSlider} = sliderData;

      if (!e.altKey || !indexSlider || indexSlider.max <= 0) {
        return false;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        const newValue = clamp(
          indexSlider.current + 1,
          indexSlider.min,
          indexSlider.max
        );
        updateIndex(newValue);
        return true;
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        const newValue = clamp(
          indexSlider.current - 1,
          indexSlider.min,
          indexSlider.max
        );
        updateIndex(newValue);
        return true;
      }

      return false;
    },
    [sliderData, updateIndex]
  );

  /**
   * Handles run navigation keyboard shortcuts (plain Arrow keys)
   */
  const handleNavigationKeys = useCallback(
    (e: KeyboardEvent): boolean => {
      const {currentRun, availableRuns} = navigation;

      // Only handle plain arrow keys (no modifiers) when there are multiple runs
      if (
        e.altKey ||
        e.metaKey ||
        e.ctrlKey ||
        !currentRun ||
        availableRuns.length <= 1
      ) {
        return false;
      }

      const currentRunIndex = availableRuns.findIndex(
        run => run.name === currentRun.name
      );

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        // Navigate to previous run if not at the first run
        if (currentRunIndex > 0) {
          const previousRun = availableRuns[currentRunIndex - 1];
          selectRun(previousRun);
        }
        return true;
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        // Navigate to next run if not at the last run
        if (currentRunIndex < availableRuns.length - 1) {
          const nextRun = availableRuns[currentRunIndex + 1];
          selectRun(nextRun);
        }
        return true;
      }

      return false;
    },
    [navigation, selectRun]
  );

  /**
   * Convenience function that tries all keyboard handlers in order.
   * Returns true if any handler processed the event.
   */
  const handleModalKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      // Try each handler in order - first one to handle it wins
      return (
        handleEscapeKey(e) ||
        handleStepSliderKeys(e) ||
        handleIndexSliderKeys(e) ||
        handleNavigationKeys(e)
      );
    },
    [
      handleEscapeKey,
      handleStepSliderKeys,
      handleIndexSliderKeys,
      handleNavigationKeys,
    ]
  );

  return {
    handleEscapeKey,
    handleStepSliderKeys,
    handleIndexSliderKeys,
    handleNavigationKeys,
    handleModalKeyDown,
  };
};
